import { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, GripVerticalIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import type {
  FormCreateInput,
  FormUpdateInput,
  CollectionColumn,
  Form,
  ResourceOperation,
} from '@/types/entities';

interface FormSchemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: FormCreateInput | FormUpdateInput) => Promise<void>;
  onCancel?: () => void;
  form?: Form;
  title?: string;
  description?: string;
}

const COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'integer', label: 'Integer' },
  { value: 'double', label: 'Number' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'image', label: 'Image' },
  { value: 'richtext', label: 'Rich Text' },
] as const;

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

interface ColumnFormData {
  name: string;
  label: string;
  type: string;
  required: boolean;
  isPrivate: boolean;
  default: string;
}

export function FormSchemaDialog({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  form,
  title = 'Form',
  description = 'Define the schema for this form.',
}: FormSchemaDialogProps) {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [ops, setOps] = useState<ResourceOperation[]>(ALL_OPERATIONS);
  const [columns, setColumns] = useState<ColumnFormData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!form;

  useEffect(() => {
    if (form) {
      setName(form.name);
      setDisplayName(form.display_name);
      setEndpoint(form.endpoint);
      setEnabled(form.details.enabled !== false);
      const configuredOps = form.details.ops?.length ? form.details.ops : ALL_OPERATIONS;
      const normalizedOps = Array.from(new Set<ResourceOperation>(configuredOps));
      setOps(ALL_OPERATIONS.filter((operation) => normalizedOps.includes(operation)));
      setColumns(
        form.details.columns.map((col) => ({
          name: col.name,
          label: col.label,
          type: col.type,
          required: col.required || false,
          isPrivate: col.isPrivate === true,
          default: String(col.default ?? ''),
        }))
      );
    } else {
      setName('');
      setDisplayName('');
      setEndpoint('');
      setEnabled(true);
      setOps(ALL_OPERATIONS);
      setColumns([]);
    }
    setError(null);
  }, [form]);

  const toggleOperation = (operation: ResourceOperation, checked: boolean) => {
    setOps((currentOps) => {
      const next = checked
        ? [...currentOps, operation]
        : currentOps.filter((value) => value !== operation);

      const normalized = Array.from(new Set<ResourceOperation>(next));
      return ALL_OPERATIONS.filter((value) => normalized.includes(value));
    });
  };

  const addColumn = () => {
      setColumns([
        ...columns,
      { name: '', label: '', type: 'text', required: false, isPrivate: false, default: '' },
    ]);
  };

  const removeColumn = (index: number) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const updateColumn = (index: number, updates: Partial<ColumnFormData>) => {
    setColumns(
      columns.map((col, i) => (i === index ? { ...col, ...updates } : col))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Form name is required');
      return;
    }
    if (!/^[a-z0-9_]+$/i.test(name)) {
      setError('Form name must contain only letters, numbers, and underscores');
      return;
    }
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }
    if (!endpoint.trim()) {
      setError('Endpoint is required');
      return;
    }
    if (!/^[a-z0-9_-]+$/i.test(endpoint)) {
      setError('Endpoint must contain only letters, numbers, hyphens, and underscores');
      return;
    }
    if (columns.length === 0) {
      setError('At least one column is required');
      return;
    }

    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (!col.name.trim()) {
        setError(`Column ${i + 1}: Name is required`);
        return;
      }
      if (!/^[a-z0-9_]+$/i.test(col.name)) {
        setError(`Column ${i + 1}: Name must contain only letters, numbers, and underscores`);
        return;
      }
      if (!col.label.trim()) {
        setError(`Column ${i + 1}: Label is required`);
        return;
      }
      const duplicates = columns.filter((c, j) => c.name.trim() === col.name.trim() && j !== i);
      if (duplicates.length > 0) {
        setError(`Column "${col.name}": Duplicate column name`);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const formColumns: CollectionColumn[] = columns.map((col) => {
        const column: CollectionColumn = {
          name: col.name.trim(),
          label: col.label.trim(),
          type: col.type,
        };
        if (col.required) column.required = true;
        if (col.isPrivate) column.isPrivate = true;
        if (col.default.trim()) {
          if (col.type === 'integer') {
            column.default = parseInt(col.default, 10);
          } else if (col.type === 'double') {
            column.default = parseFloat(col.default);
          } else if (col.type === 'boolean') {
            column.default = col.default.toLowerCase() === 'true';
          } else {
            column.default = col.default;
          }
        }
        return column;
      });

      const data: FormCreateInput | FormUpdateInput = {
        name: name.trim(),
        display_name: displayName.trim(),
        endpoint: endpoint.trim(),
        details: {
          columns: formColumns,
          enabled,
          ops: ALL_OPERATIONS.filter((operation) => ops.includes(operation)),
        },
      };
      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit' : 'Create'} {title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="name">Form Name</Label>
              <Input
                id="name"
                placeholder="contact_form"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Letters, numbers, and underscores only (used for database table)
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                placeholder="Contact Form"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Human-readable name for the UI
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="endpoint">Endpoint</Label>
              <Input
                id="endpoint"
                placeholder="contact"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Used in the public URL: /api/v1/:tenantSlug/forms/{endpoint}/submit
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="form-enabled">Public Submit Endpoint</Label>
              <div className="flex items-center gap-2 rounded-lg border p-3">
                <Checkbox
                  id="form-enabled"
                  checked={enabled}
                  onCheckedChange={(checked) => setEnabled(!!checked)}
                  disabled={isSubmitting}
                />
                <Label htmlFor="form-enabled" className="text-sm">
                  Enabled (allow POST /public/tenants/:tenantSlug/forms/:endpoint/submit)
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Allowed Operations</Label>
              <div className="grid grid-cols-2 gap-2 rounded-lg border p-3">
                {ALL_OPERATIONS.map((operation) => {
                  return (
                    <div key={operation} className="flex items-center gap-2">
                      <Checkbox
                        id={`form-op-${operation}`}
                        checked={ops.includes(operation)}
                        onCheckedChange={(checked) => toggleOperation(operation, !!checked)}
                        disabled={isSubmitting}
                      />
                      <Label htmlFor={`form-op-${operation}`} className="text-sm capitalize">
                        {operation}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Fields</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addColumn}
                  disabled={isSubmitting}
                  className="gap-1"
                >
                  <PlusIcon className="size-3" />
                  Add Field
                </Button>
              </div>

              {columns.length === 0 ? (
                <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground">
                  No fields defined. Click "Add Field" to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {columns.map((column, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-3 space-y-2 bg-muted/20"
                    >
                      <div className="flex items-center gap-2">
                        <GripVerticalIcon className="size-4 text-muted-foreground cursor-move" />
                        <Input
                          placeholder="field_name"
                          value={column.name}
                          onChange={(e) => updateColumn(index, { name: e.target.value })}
                          disabled={isSubmitting}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeColumn(index)}
                          disabled={isSubmitting}
                        >
                          <TrashIcon className="size-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Label"
                          value={column.label}
                          onChange={(e) => updateColumn(index, { label: e.target.value })}
                          disabled={isSubmitting}
                        />
                        <Select
                          value={column.type}
                          onValueChange={(value) => updateColumn(index, { type: value })}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {COLUMN_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`required-${index}`}
                            checked={column.required}
                            onCheckedChange={(checked) =>
                              updateColumn(index, { required: !!checked })
                            }
                            disabled={isSubmitting}
                          />
                          <Label htmlFor={`required-${index}`} className="text-sm">
                            Required
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`private-${index}`}
                            checked={column.isPrivate}
                            onCheckedChange={(checked) =>
                              updateColumn(index, { isPrivate: !!checked })
                            }
                            disabled={isSubmitting}
                          />
                          <Label htmlFor={`private-${index}`} className="text-sm">
                            Private (CMS only)
                          </Label>
                        </div>
                        <Input
                          placeholder="Default value"
                          value={column.default}
                          onChange={(e) => updateColumn(index, { default: e.target.value })}
                          disabled={isSubmitting}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                onCancel?.();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
